import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  LinearProgress,
  Alert,
  Stack,
  Divider,
  alpha,
  CircularProgress
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Warning,
  Receipt,
  Description,
  Business,
  Star,
  ArrowForward
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { subscriptionService } from '../services/api';

const Subscription = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const didAutoSyncRef = useRef(false);

  useEffect(() => {
    fetchSubscriptionData();
  }, []);

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      let [status, currentUsage] = await Promise.all([
        subscriptionService.getStatus(),
        subscriptionService.getUsage(),
      ]);

      const isProNow = status?.subscriptionPlan === 'pro' || status?.subscriptionPlan === 'enterprise';
      if (!isProNow && !didAutoSyncRef.current) {
        didAutoSyncRef.current = true;
        try {
          await subscriptionService.syncLatest();
          window.dispatchEvent(new Event('subscription:refresh'));
          status = await subscriptionService.getStatus();
        } catch (syncError) {
          if (process.env.NODE_ENV !== 'production') {
            console.warn('Auto sync latest failed:', syncError?.message || syncError);
          }
        }
      }

      setSubscription(status);
      setUsage(currentUsage);
    } catch (err) {
      console.error('Erreur chargement abonnement:', err);
      setError('Impossible de charger les informations d\'abonnement');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async () => {
    try {
      setActionLoading(true);
      const response = await subscriptionService.createCheckoutSession('pro');

      // Rediriger vers Stripe Checkout
      window.location.href = response.url;
    } catch (err) {
      console.error('Erreur upgrade:', err);
      setError('Impossible de démarrer le processus de paiement');
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler votre abonnement ? Vous conserverez l\'accès jusqu\'à la fin de la période payée.')) {
      return;
    }

    try {
      setActionLoading(true);
      await subscriptionService.cancel();

      alert('Abonnement annulé avec succès');
      fetchSubscriptionData();
    } catch (err) {
      console.error('Erreur annulation:', err);
      setError('Impossible d\'annuler l\'abonnement');
    } finally {
      setActionLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      setActionLoading(true);
      const response = await subscriptionService.createPortalSession();
      window.location.href = response.url;
    } catch (err) {
      console.error('Erreur portail:', err);
      setError('Impossible d\'accéder au portail de gestion');
      setActionLoading(false);
    }
  };

  const getStatusIcon = () => {
    if (!subscription) return <Warning />;
    
    switch (subscription.subscriptionStatus) {
      case 'active':
      case 'trial':
        return <CheckCircle sx={{ color: '#4CAF50' }} />;
      case 'canceled':
      case 'past_due':
        return <Warning sx={{ color: theme.palette.primary.main }} />;
      default:
        return <Cancel sx={{ color: '#f44336' }} />;
    }
  };

  const getStatusText = () => {
    if (!subscription) return 'Inconnu';
    
    switch (subscription.subscriptionStatus) {
      case 'active':
        return 'Actif';
      case 'trial':
        return `Essai gratuit (${subscription.daysLeft} jours restants)`;
      case 'canceled':
        return 'Annulé';
      case 'past_due':
        return 'Paiement en retard';
      default:
        return subscription.subscriptionStatus;
    }
  };

  const getPlanName = () => {
    if (!subscription) return 'Gratuit';
    
    switch (subscription.subscriptionPlan) {
      case 'pro':
        return 'Pro';
      case 'enterprise':
        return 'Enterprise';
      default:
        return 'Gratuit';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const isPro = subscription?.subscriptionPlan === 'pro' || subscription?.subscriptionPlan === 'enterprise';
  const isFree = !isPro;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight="bold" mb={4}>
        Mon Abonnement
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Carte Abonnement Actuel */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {getStatusIcon()}
                <Typography variant="h6" fontWeight="bold" ml={1}>
                  Plan {getPlanName()}
                </Typography>
              </Box>

              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Statut
                  </Typography>
                  <Typography variant="body1" fontWeight="600">
                    {getStatusText()}
                  </Typography>
                </Box>

                {subscription?.currentPeriodEnd && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Renouvellement
                    </Typography>
                    <Typography variant="body1">
                      {new Date(subscription.currentPeriodEnd).toLocaleDateString('fr-FR')}
                    </Typography>
                  </Box>
                )}

                <Divider />

                {isFree ? (
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    startIcon={<Star />}
                    onClick={handleUpgrade}
                    disabled={actionLoading}
                    sx={{
                      background: 'linear-gradient(135deg, #1F2937 0%, #4B5563 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #4B5563 0%, #1F2937 100%)',
                      }
                    }}
                  >
                    {actionLoading ? 'Chargement...' : 'Passer à Pro'}
                  </Button>
                ) : (
                  <Stack spacing={1}>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={handleManageSubscription}
                      disabled={actionLoading}
                    >
                      Gérer mon abonnement
                    </Button>
                    <Button
                      variant="text"
                      color="error"
                      fullWidth
                      onClick={handleCancel}
                      disabled={actionLoading}
                    >
                      Annuler l'abonnement
                    </Button>
                  </Stack>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Carte Utilisation */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" mb={3}>
                Utilisation ce mois
              </Typography>

              {usage && (
                <Stack spacing={3}>
                  {/* Factures */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Receipt sx={{ mr: 1, color: '#64748B' }} />
                      <Typography variant="body1" fontWeight="600">
                        Factures
                      </Typography>
                    </Box>
                    {usage.usage.invoices.unlimited ? (
                      <Chip label="Illimité" size="medium" sx={{ bgcolor: '#1F2937', color: '#fff', fontWeight: 600, fontSize: '0.9rem', py: 2.5 }} />
                    ) : (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            {usage.usage.invoices.current} / {usage.usage.invoices.limit}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {Math.round((usage.usage.invoices.current / usage.usage.invoices.limit) * 100)}%
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={Math.min((usage.usage.invoices.current / usage.usage.invoices.limit) * 100, 100)}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: alpha('#A7AFB9', 0.25),
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: usage.usage.invoices.current >= usage.usage.invoices.limit ? '#f44336' : '#1F2937',
                              borderRadius: 4
                            }
                          }}
                        />
                      </>
                    )}
                  </Box>

                  {/* Devis */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Description sx={{ mr: 1, color: '#64748B' }} />
                      <Typography variant="body1" fontWeight="600">
                        Devis
                      </Typography>
                    </Box>
                    {usage.usage.quotes.unlimited ? (
                      <Chip label="Illimité" size="medium" sx={{ bgcolor: '#1F2937', color: '#fff', fontWeight: 600, fontSize: '0.9rem', py: 2.5 }} />
                    ) : (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            {usage.usage.quotes.current} / {usage.usage.quotes.limit}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {Math.round((usage.usage.quotes.current / usage.usage.quotes.limit) * 100)}%
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={Math.min((usage.usage.quotes.current / usage.usage.quotes.limit) * 100, 100)}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: alpha('#A7AFB9', 0.25),
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: usage.usage.quotes.current >= usage.usage.quotes.limit ? '#f44336' : '#1F2937',
                              borderRadius: 4
                            }
                          }}
                        />
                      </>
                    )}
                  </Box>

                  {/* Clients */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Business sx={{ mr: 1, color: '#64748B' }} />
                      <Typography variant="body1" fontWeight="600">
                        Clients
                      </Typography>
                    </Box>
                    {usage.usage.companies.unlimited ? (
                      <Chip label="Illimité" size="medium" sx={{ bgcolor: '#1F2937', color: '#fff', fontWeight: 600, fontSize: '0.9rem', py: 2.5 }} />
                    ) : (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            {usage.usage.companies.current} / {usage.usage.companies.limit}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {Math.round((usage.usage.companies.current / usage.usage.companies.limit) * 100)}%
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={Math.min((usage.usage.companies.current / usage.usage.companies.limit) * 100, 100)}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: alpha('#A7AFB9', 0.25),
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: usage.usage.companies.current >= usage.usage.companies.limit ? '#f44336' : '#1F2937',
                              borderRadius: 4
                            }
                          }}
                        />
                      </>
                    )}
                  </Box>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* CTA Upgrade si FREE */}
      {isFree && (
        <Card sx={{ mt: 3, background: `linear-gradient(135deg, ${alpha('#A7AFB9', 0.12)} 0%, ${alpha('#A7AFB9', 0.06)} 100%)` }}>
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography variant="h5" fontWeight="bold" mb={1}>
                  Passez à Pro et débloquez tout le potentiel
                </Typography>
                <Typography variant="body1" color="text.secondary" mb={2}>
                  Factures et devis illimités, relances automatiques, rapprochement bancaire via import CSV et notes de frais avancées.
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip label="30 jours d'essai gratuit" sx={{ bgcolor: '#1F2937', color: '#fff', fontWeight: 600, fontSize: '1rem', py: 2.5, px: 1 }} />
                  <Chip label="14,99€ net/mois" />
                  <Chip label="Sans engagement" />
                </Stack>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Offre fondateur : 14,99€ à vie pour les 100 premiers abonnés (prix bloqué).
                </Typography>
              </Grid>
              <Grid item xs={12} md={4} textAlign="center">
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward />}
                  onClick={() => navigate('/pricing')}
                  sx={{
                    background: 'linear-gradient(135deg, #1F2937 0%, #4B5563 100%)',
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4B5563 0%, #1F2937 100%)',
                    }
                  }}
                >
                  Voir les plans
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default Subscription;
